import { AsyncPipe, NgClass } from '@angular/common';
import { Component, EventEmitter, HostListener, inject, Input, Output, ViewChild } from '@angular/core'
import { NgCircleProgressModule } from 'ng-circle-progress'
import { DialogComponent, TileComponent } from '@nx-superprep/shared'
import { DateOnly, durationFormatter, resolveProperty, log, toDefaultNumber } from '@nx-superprep/utils'
import { PopoverDirective, PopoverModule } from 'ngx-bootstrap/popover'
import { LessonTile } from '@nx-superprep/study'
import { DrawerState } from '../../../drawer/drawer.component'
import { MatDialog } from '@angular/material/dialog'
import { firstValueFrom } from 'rxjs'
import { ActionMenuComponent } from "./action-menu/action-menu.component";
import { MatIconModule } from '@angular/material/icon'

export type ActionNameType = 'detail'|'try'|'study'|'add'|'del'|'options'|'none'

@Component({
  selector: 'pop-ui-lesson-tile-study',
  standalone: true,
  imports: [
    NgClass, AsyncPipe,
    NgCircleProgressModule,
    MatIconModule,
    PopoverModule,
    ActionMenuComponent
  ],
  templateUrl: './study.component.html',
  styleUrls: ['./study.component.scss'],
  queries: {
    "popoverRef": new ViewChild("_study_popover"),
  },
})
export class LessonTileStudyComponent extends TileComponent {

  // close menu on events that scroll the page
  @HostListener('body:touchmove', ['$event'])
  @HostListener('body:keydown.ArrowDown', ['$event'])
  @HostListener('body:keydown.ArrowUp', ['$event'])
  @HostListener('body:mousewheel', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onEvent(event: Event) { this.popoverRef?.hide() }

  override get rootClassName() {
    return `${this.isDimmed?'dimmed':''} ${super.rootClassName}`
  }
  override set rootClassName(value) {
    super.rootClassName = value
  }

  @Input() state?: DrawerState
  @Output() stateChange = new EventEmitter<DrawerState>()

  private _submitted = false

  private _tile!: LessonTile
  @Input()
  public get tile(): LessonTile {
    return this._tile
  }
  public set tile(value: LessonTile) {
    this._tile = value
    this.element = { content: value }
  }

  get status() {
    let _status = ''
    if (this.isStudyTile) {
      if (this.isFinished) {
        const expected = durationFormatter(this.tile.expectedStudyDate || 0, {short:false, single:true, granularity:'day'})
        _status = `• ${expected.startsWith('0') ? 'Completed' : `${expected.endsWith('ago') ? 'Last study' : 'Upcoming'} ${expected}`}`
      } else {
        if (this.isInPast || this.isDone) {
          _status = '• Incomplete'
        } else {
          _status = this.tile.estimated > 0 ? `• ${durationFormatter(this.tile.estimated, {short:true, single:true, granularity:'minute'})}` : 'Done'
        }
      }
    } else if (this.isPerformance) {
        _status = this.tile.subjects[0]?.title ? `• ${this.tile.subjects[0].title}` : ''
    } else {
        _status = this.tile.description ?? ''
    }
    return _status
  }

  spinner = {src: '/assets/spinner.svg', alt: 'spinner'}

  get actions() { return this.isLibrary ? ['action2'] : this.isInPast ? ['action1'] :  ['action2'] }

  get radius() {
    return 29
  }

  get imageHeight() {
    return 50
  }

  get imageWidth() {
    return 50
  }

  get outerStrokeWidth() {
    return 5
  }

  get outerStrokeColor() {
    return this.tile.progress >= 1 ? '#44B34B' : '#E2C537'
  }

  get outerStrokeLinecap() {
    return 'round'//'square'
  }

  get innerStrokeWidth() {
    return 5
  }

  get innerStrokeColor() {
    return '#F0F0F0'
  }

  get percent() {
    return this.isFinished ? 100 : 100 * (Number(this.tile.progress)||0)
  }

  get percentTitle() {
    return `${toDefaultNumber(this.percent, 0).toFixed(0)}%`
  }

  get label() {
    return ''
  }

  get activeTab() {
    return (this.state?.headerState.activeTab ?? '').toLowerCase()
  }

  get date() {
    return this.state?.contentState?.date ?? new DateOnly()
  }

  isActiveTab(name: string) {
    return this.activeTab === name.toLowerCase()
  }

  get isTodo() {
    return this.isActiveTab('Active')
  }
  get isDone() {
    return this.isActiveTab('Done')
  }
  get isAll() {
    return this.isActiveTab('All')
  }

  get isInToday() {
    return this.date.isToday()
  }
  get isInPast() {
    return this.date.isBefore(new DateOnly())
  }
  get isInFuture() {
    return this.date.isAfter(new DateOnly())
  }

  get isDayPlan() {
    return this.state?.type === 'dayplan-lessons'
  }

  get isLibrary() {
    return this.state?.type === 'library-lessons'
  }

  get isPerformance() {
    return this.isPerformanceStats || this.isPerformanceCharts
  }

  get isPerformanceStats() {
    return this.state?.type === 'performance-stats'
  }

  get isPerformanceCharts() {
    return this.state?.type === 'performance-charts'
  }

  get isLessonCompletion() {
    return this.state?.type === 'lesson-completion'
  }

  get isOnboarding() {
    return this.state?.type === 'onboarding-lessons'
  }

  // evaluators

  get isValid() {
    return resolveProperty(this.tile.isValid);
  }

  get isNew() {
    return resolveProperty(this.tile.isNew)
  }

  get isAssigned() {
    return resolveProperty(this.tile.isAssigned)
  }

  get isSubscribed() {
    return resolveProperty(this.tile.subscribed)
  }

  get isFinished() {
    return resolveProperty(this.tile.finished)
  }

  get isDimmed() {
    return this.isAll && (this.isFinished || this._submitted)
  }

  // action menu available
  get isMenuable() {
    return  !this._submitted && this.isValid && (this.isLibraryTile || (this.isStudyTile && !this.isInFuture))
  }

  // action on click available
  get isActionable() {
    return !this._submitted && (this.isOnboardingTile || this.isPerformanceTile || this.isLibraryTile || (this.isStudyTile && !this.isInFuture))
  }

  // action has an implicit select
  isSelectable(action:string) {
    return ['detail'].includes(action)
  }

  get isStudyTile() {
    return this.isDayPlan
  }

  get isLibraryTile() {
    return this.isLibrary
  }

  get isPerformanceTile() {
    return this.isPerformance
  }

  get isOnboardingTile() {
    return this.isOnboarding
  }

  get context() {
    return this.isPerformanceTile ? 'performance' : this.isStudyTile ? 'study' : this.isLibraryTile ? 'library' : ''
  }

  iconRounded(action: string) {
    return action !== 'options'
  }

  options = { name:'options', label: '', iconURL:'/assets/icons/icon-vertical-dots.svg', iconALT: 'icon-vertical-dots', iconStrokeColor: 'transparent', active: false }
  detail = { name: 'detail', label:'Go to details', iconURL:'/assets/icons/icon-vertical-dots.svg', iconALT: 'icon-vertical-dots', iconStrokeColor: 'transparent', active: false }
  study = { name: 'study', label: 'Study unit', iconURL: '/assets/icons/iconplaybutton.svg', iconALT: 'iconplaybutton', iconStrokeColor: 'transparent', active: false }
  try = { name: 'try', label: 'Try unit', iconURL: '/assets/icons/iconquiz.svg', iconALT: 'iconquiz', iconStrokeColor: 'transparent', active: false }
  add = { name: 'add', label: 'Add unit', iconURL: '/assets/icons/iconadd.svg', iconALT: 'iconadd', iconStrokeColor: 'transparent', active: false }
  del = { name:'del', label: 'Remove unit', iconURL:'/assets/icons/iconcheckmark-filled.svg', iconALT: 'iconcheckmark', iconStrokeColor: 'green', active: false }
  soon = { name:'soon', label: 'Coming Soon', iconURL:'/assets/icons/iconclock.svg', iconALT: 'iconcheckmark', iconStrokeColor: 'transparent', active: false }

  allActions = [this.options, this.detail, this.study, this.try, this.add, this.del]

  // default action when the tile is clicked
  get defaultAction() {
    return this.isPerformance
    ? this.detail
    : this.isLibrary
      ? this.isValid ? this.detail : this.soon
      : this.isOnboarding
        ? this.isSubscribed ? this.del : this.add
        : this.isInToday
          ? (this.isDone || (this.isAll && this.isFinished)) ? this.detail : this.study
          : this.isInPast ? this.detail : this.options
  }

  get action() {
    return this.isLibrary
    ? this.isSubscribed ? this.del : this.options
    : this.isOnboarding
      ? this.isSubscribed ? this.del : this.add
      : this.options
  }

  get isOptions() {
    return this.action.name === this.options.name
  }

  get actionMenu() {
    const baseMenu = [this.detail]

    if (this.isLibrary) {
      if (!this.isValid) return [this.soon]

      const libraryMenu = [...baseMenu, this.study]
      //if (this.isAssigned) return libraryMenu
      return this.isSubscribed ? [...libraryMenu, this.del] : [...baseMenu, this.try, this.add]
    }

    //const shouldIncludeDelete = !this.isInToday || this.isDone || this.isFinished || !this.isAssigned
    const shouldIncludeDelete = this.isInToday || this.isDone || this.isFinished
    return shouldIncludeDelete ? [...baseMenu, this.del] : baseMenu
  }

  onAction(action: string) {
    if (!this.isActionAllowed(action)) return;
    this.handleActionExecution(action);
  }

  isActionAllowed(action: string): boolean {
    if (this._submitted || !this.tile.id || !this.isActionable) return false;
    return this.allActions.some(a => a.name === action);
  }

  handleActionExecution(action: string) {
    this.askForConsent(this.isAssigned && action === 'del')
      .then(consent => this.executeAction(consent, action))
      .finally(() => this._submitted = false);
  }

  executeAction(consent: boolean, action: string) {
    if (!consent) return Promise.resolve(false);
    const actionFunction = this.getActionFunction(action);
    return actionFunction(action).then(success => this.updateContentState(success, action));
  }

  getActionFunction(action: string) {
    return this.isPerformanceTile && action === 'detail'
    ? () => { if (this.state) { this.state.contentState.lesson = this.tile }; return Promise.resolve(true) }
    : (this.state?.contentState.action ?? this.tile.action) ?? (() => Promise.resolve(false))
  }

  updateContentState(success: boolean, action: string, tile = this.tile, state = this.state, isSelectable = this.isSelectable(action), allActions = this.allActions, popoverRef = this.popoverRef) {
    let _result: Promise<boolean> = Promise.resolve(success)
    popoverRef?.hide()
    allActions.forEach(a => a.active = false)
    if (!success || !state) { return _result }
    // handle selection update
    if ((this.isDayPlan || this.isWeb || action === 'detail') && state.contentState.select) { _result = state.contentState.select(tile) }
    tile.selected = !tile.selected
    state.contentState.selected = isSelectable ? tile.title : undefined
    // handle dayplan removal of deleted tiles
    if (this.isDayPlan && action === 'del' && state.contentState.tiles) {
      const found = state.contentState.tiles.findIndex(t => t.id === tile.id)
      if (found >= 0) { state.contentState.tiles.splice(found, 1) }
    }
    return _result.finally(() => this.stateChange.emit(state))
  }

  readonly dialog = inject(MatDialog);

  askForConsent(shouldAsk = false): Promise<boolean> {
    if (!shouldAsk) { return Promise.resolve(true)}
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {title:'Remove Unit ?', message:'This is a teacher-assigned unit. Are you sure you want to remove it ?', accept: 'Remove', decline: 'Cancel'},
    });

    return firstValueFrom(dialogRef.afterClosed())
  }

  public popoverRef!: PopoverDirective
  onMouseout(e:MouseEvent) {
    log.debug(e.target)
    this.popoverRef?.hide()
  }

  constructor () {
    super()
    super.rootClassName = 'lesson-tile-study'
  }
}
